// TanStack Table + Shadcn UI DataTable bileşeni (virtualized scroll destekli)
'use client';

import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, Fragment, type ReactNode } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  type RowSelectionState,
  type ColumnFiltersState,
  type ExpandedState,
  type ColumnOrderState,
  type Row,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Badge, type BadgeProps } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown, Filter, X, Plus, CircleCheck, CircleX, Equal, Calendar as CalendarIcon, Settings2, GripVertical } from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { Switch } from '../ui/switch';
import { SlideOverSheet } from './slide-over-sheet';
import { startOfDay, endOfDay, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths, format as formatDate2 } from 'date-fns';
import { tr } from 'date-fns/locale';
import { cn } from '../../lib/utils';

type DateRange = { from: Date | undefined; to?: Date | undefined };

// Reorder/visibility'ye tabi olmayan sistem kolonları.
const UTILITY_COLUMNS = new Set(['select', 'actions', 'expand']);
// Bu utility kolonlar SAĞDA (trailing) kalır; select/expand ise SOLDA (leading).
// 'actions' butonları daima en sağda görünür — sıralama senkronunda sona sabitlenir.
const TRAILING_UTILITY_COLUMNS = new Set(['actions']);

export interface DataTableFilterOption {
  value: string;
  label: string;
  group?: string;
  groupFilterType?: 'number';
}

export interface NumberFilterValue {
  mode: 'eq' | 'lt' | 'gt';
  value: number;
}

export interface SelectFilterValue {
  mode: 'include' | 'exclude';
  values: string[];
  groupModes?: Record<string, 'include' | 'exclude'>;
  numberFilter?: NumberFilterValue;
}

export interface DateFilterValue {
  from?: string; // ISO date string (YYYY-MM-DD)
  to?: string;   // ISO date string (YYYY-MM-DD)
}

export interface DataTableFilterTab {
  value: string;
  label: string;
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  className?: string;
  style?: React.CSSProperties;
  count?: number;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  searchable?: boolean;
  searchPlaceholder?: string;
  pageSize?: number;
  pageSizeOptions?: number[];
  onRowClick?: (row: TData) => void;
  onRowHover?: (row: TData) => void;
  onRowHoverEnd?: () => void;
  selectable?: boolean;
  onSelectionChange?: (selectedRows: TData[]) => void;
  /** Tümü seç modu: true iken yüklü tüm satırlar işaretli tutulur; infinite scroll
   *  ile yeni yüklenen satırlar da otomatik seçilir. */
  selectAll?: boolean;
  emptyMessage?: string;
  defaultSorting?: SortingState;
  filterTabs?: DataTableFilterTab[];
  filterKey?: string;
  allLabel?: string;
  toolbarActions?: ReactNode;
  toolbarRight?: (filteredRows: TData[], hasActiveFilters: boolean) => ReactNode;
  onAddNew?: () => void;
  addNewLabel?: string;
  renderSubRow?: (row: TData) => ReactNode;
  selectionKey?: number;
  footerContent?: (filteredRows: TData[], hasActiveFilters: boolean) => ReactNode;
  infiniteScroll?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onExternalSearch?: (term: string) => void;
  externalSearchValue?: string;
  onFilterTabChange?: (value: string | null) => void;
  activeFilterValue?: string | null;
  totalCount?: number;
  onColumnFilterChange?: (filters: ColumnFiltersState) => void;
  /** Dışarıdan sütun filtresi set etmek için — belirtilen sütunlar override edilir, diğerleri korunur */
  columnFilterOverrides?: ColumnFiltersState;
  serverPagination?: {
    currentPage: number;
    totalPages: number;
    totalRows: number;
    onPageChange: (page: number) => void;
  };
  loading?: boolean;
  /** Virtualized scroll: sadece görünen satırlar + buffer DOM'da render edilir */
  virtualized?: boolean;
  /** Virtualized scroll konteyner yüksekliği (default: 600px) */
  virtualContainerHeight?: number;
  /** Tahmini satır yüksekliği (default: 56px) */
  virtualRowHeight?: number;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  searchable = true,
  searchPlaceholder = 'Ara...',
  pageSize = 10,
  pageSizeOptions = [10, 25, 50],
  onRowClick,
  onRowHover,
  onRowHoverEnd,
  selectable = false,
  onSelectionChange,
  selectAll = false,
  emptyMessage = 'Kayıt bulunamadı.',
  defaultSorting = [],
  filterTabs,
  filterKey,
  allLabel = 'Hepsi',
  toolbarActions,
  toolbarRight,
  onAddNew,
  addNewLabel = 'Yeni Ekle',
  renderSubRow,
  selectionKey,
  footerContent,
  infiniteScroll = false,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  onExternalSearch,
  externalSearchValue,
  onFilterTabChange,
  activeFilterValue,
  totalCount,
  onColumnFilterChange,
  columnFilterOverrides,
  serverPagination,
  loading = false,
  virtualized = false,
  virtualContainerHeight = 600,
  virtualRowHeight = 56,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Reset row selection when selectionKey changes (e.g., after bulk actions)
  useEffect(() => {
    if (selectionKey !== undefined) setRowSelection({});
  }, [selectionKey]);

  const [activeFilterInternal, setActiveFilterInternal] = useState<string | null>(null);
  const activeFilter = onFilterTabChange ? (activeFilterValue ?? null) : activeFilterInternal;
  const setActiveFilter = onFilterTabChange
    ? (val: string | null) => onFilterTabChange(val)
    : setActiveFilterInternal;
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  // Dışarıdan sütun filtresi override: belirtilen sütunlar güncellenir, diğerleri korunur
  // Referans değişimi = uygulama tetiği. Parent her setState'te yeni array yollamalı.
  // value: null/undefined olan entry'ler ilgili kolondan filtreyi KALDIRIR.
  useEffect(() => {
    if (!columnFilterOverrides) return;
    setColumnFilters((prev) => {
      const overrideIds = new Set(columnFilterOverrides.map((f) => f.id));
      const kept = prev.filter((f) => !overrideIds.has(f.id));
      const additions = columnFilterOverrides.filter((f) => f.value != null);
      const next = [...kept, ...additions];
      // Gerçek bir değişim yoksa referansı koru (gereksiz re-render önle)
      if (
        next.length === prev.length &&
        next.every((f, i) => prev[i]?.id === f.id && prev[i]?.value === f.value)
      ) {
        return prev;
      }
      return next;
    });
  }, [columnFilterOverrides]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);

  // Compute counts per filter value (memoized)
  const filterCounts = useMemo(() => {
    if (!filterTabs || !filterKey) return {};
    const counts: Record<string, number> = {};
    for (const item of data) {
      const val = (item as Record<string, unknown>)[filterKey] as string;
      if (val) counts[val] = (counts[val] || 0) + 1;
    }
    return counts;
  }, [data, filterTabs, filterKey]);

  // Filter data (memoized to prevent infinite re-render loop)
  const filteredData = useMemo(
    () => activeFilter && filterKey
      ? data.filter((item) => (item as Record<string, unknown>)[filterKey] === activeFilter)
      : data,
    [data, activeFilter, filterKey]
  );

  // Auto-hide columns with meta.hidden
  const defaultColumnVisibility = useMemo(() => {
    const vis: VisibilityState = {};
    columns.forEach((col) => {
      const id = (col as any).accessorKey ?? (col as any).id;
      if (id && (col as any).meta?.hidden) {
        vis[id] = false;
      }
    });
    return vis;
  }, [columns]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(defaultColumnVisibility);

  // Column order state — initialized after allColumns is built (see below)
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);

  // Selection checkbox column — utility kolon: reorder/visibility kapalı, daima başta
  const selectionColumn: ColumnDef<TData, TValue> = {
    id: 'select',
    size: 40,
    enableSorting: false,
    enableHiding: false,
    header: ({ table: t }) => (
      <Checkbox
        checked={t.getIsAllRowsSelected() || (t.getIsSomeRowsSelected() && 'indeterminate')}
        onCheckedChange={(value) => t.toggleAllRowsSelected(!!value)}
        aria-label="Tümünü seç"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        onClick={(e) => e.stopPropagation()}
        aria-label="Satır seç"
      />
    ),
  };

  const allColumns = selectable ? [selectionColumn, ...columns] : columns;

  // Stable column id list derived from the columns prop
  const columnIds = useMemo(() => {
    const ids = columns.map((col) => (col as any).accessorKey ?? (col as any).id ?? '');
    return selectable ? ['select', ...ids] : ids;
  }, [columns, selectable]);

  // Sync column order when columns change (keeps user reordering intact).
  // Utility kolonlar (select, expand) reorder'a tabi değil — her zaman başta tutulur.
  useEffect(() => {
    setColumnOrder((prev) => {
      if (prev.length === 0) return columnIds;
      const known = new Set(columnIds);
      // Leading utility (select/expand) solda; trailing utility (actions) sağda kalır.
      const leadingIds = columnIds.filter((id) => UTILITY_COLUMNS.has(id) && !TRAILING_UTILITY_COLUMNS.has(id));
      const trailingIds = columnIds.filter((id) => TRAILING_UTILITY_COLUMNS.has(id));
      const dataIds = columnIds.filter((id) => !UTILITY_COLUMNS.has(id));
      const keptDataIds = prev.filter((id) => known.has(id) && !UTILITY_COLUMNS.has(id));
      const addedDataIds = dataIds.filter((id) => !keptDataIds.includes(id));
      const next = [...leadingIds, ...keptDataIds, ...addedDataIds, ...trailingIds];
      if (next.length === prev.length && next.every((id, i) => id === prev[i])) return prev;
      return next;
    });
  }, [columnIds]);

  const table = useReactTable({
    data: filteredData,
    columns: allColumns,
    state: {
      sorting,
      globalFilter,
      rowSelection,
      columnFilters,
      expanded,
      columnVisibility,
      columnOrder,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: renderSubRow ? getExpandedRowModel() : undefined,
    manualFiltering: !!onColumnFilterChange,
    initialState: {
      pagination: {
        pageSize: (infiniteScroll || serverPagination) ? 999999 : pageSize,
      },
    },
  });

  // Infinite scroll sentinel ref + IntersectionObserver
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const hasActiveClientFilters =
    (onFilterTabChange ? false : activeFilter !== null) ||
    (onColumnFilterChange ? false : columnFilters.length > 0) ||
    (onExternalSearch ? false : globalFilter !== '');

  // Normal infinite scroll — IntersectionObserver triggers when sentinel is visible
  // Virtualized tablolarda scroll handler VirtualizedTableContainer içinde yönetilir,
  // buradaki observer kullanılmaz (sentinel her zaman görünür olduğu için sürekli tetiklenir)
  useEffect(() => {
    if (!infiniteScroll || !hasMore || loadingMore || hasActiveClientFilters || virtualized) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMoreRef.current?.();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [infiniteScroll, hasMore, loadingMore, hasActiveClientFilters]);

  // Notify parent when column filters change (server-side filtering)
  const prevColumnFiltersRef = useRef<string>('');
  useEffect(() => {
    if (!onColumnFilterChange) return;
    const key = JSON.stringify(columnFilters);
    if (key !== prevColumnFiltersRef.current) {
      prevColumnFiltersRef.current = key;
      onColumnFilterChange(columnFilters);
    }
  }, [columnFilters, onColumnFilterChange]);

  const prevSelectionRef = useRef<string>('');
  useEffect(() => {
    if (selectable && onSelectionChange) {
      const key = JSON.stringify(rowSelection);
      if (key !== prevSelectionRef.current) {
        prevSelectionRef.current = key;
        const selectedRows = table.getFilteredSelectedRowModel().rows.map((row) => row.original);
        onSelectionChange(selectedRows);
      }
    }
  }, [rowSelection, selectable, onSelectionChange]);

  // selectAll modu: prop true iken tüm yüklü satırları seçili tut — infinite scroll
  // ile yeni yüklenen satırlar da bu yolla otomatik seçilir.
  useEffect(() => {
    if (!selectable || !selectAll) return;
    table.toggleAllRowsSelected(true);
  }, [selectAll, filteredData.length, selectable]);

  // Active column filters info for badges
  const activeColumnFilters = columnFilters.filter(
    (f) => {
      const v = f.value;
      if (!v) return false;
      // SelectFilterValue
      if (typeof v === 'object' && 'mode' in (v as any) && 'values' in (v as any)) {
        return ((v as SelectFilterValue).values.length > 0);
      }
      // NumberFilterValue
      if (typeof v === 'object' && 'mode' in (v as any) && 'value' in (v as any)) {
        return ((v as NumberFilterValue).value !== undefined);
      }
      // DateFilterValue
      if (typeof v === 'object' && ('from' in (v as any) || 'to' in (v as any))) {
        const dfv = v as DateFilterValue;
        return !!(dfv.from || dfv.to);
      }
      // Legacy array format
      if (Array.isArray(v)) return v.length > 0;
      return false;
    }
  );

  return (
    <>
    <Card>
      <CardContent className="p-0">
        <div className="border-b px-4 py-2.5 overflow-x-auto">
          <div className="flex items-center gap-2.5">
            {filterTabs && filterTabs.length > 0 && (
              <>
                {/* "All" tab */}
                <button
                  type="button"
                  onClick={() => { setActiveFilter(null); setRowSelection({}); }}
                  className="shrink-0 focus:outline-none"
                >
                  <Badge
                    variant="default"
                    className={cn(
                      'cursor-pointer text-xs transition-all',
                      activeFilter === null
                        ? 'ring-2 ring-offset-1 ring-offset-background ring-primary'
                        : 'opacity-40 hover:opacity-70'
                    )}
                  >
                    {allLabel}
                    <span className="ml-1.5 text-[10px] opacity-70">{totalCount ?? data.length}</span>
                  </Badge>
                </button>
                {filterTabs.map((tab) => {
                  const count = tab.count ?? filterCounts[tab.value] ?? 0;
                  const isActive = activeFilter === tab.value;
                  return (
                    <button
                      key={tab.value}
                      type="button"
                      onClick={() => { setActiveFilter(isActive ? null : tab.value); setRowSelection({}); }}
                      className="relative shrink-0 focus:outline-none"
                    >
                      <Badge
                        variant={tab.variant ?? 'outline'}
                        className={cn(
                          'cursor-pointer text-xs transition-all',
                          tab.className,
                          isActive
                            ? 'ring-2 ring-offset-1 ring-offset-background ring-primary'
                            : 'opacity-40 hover:opacity-70'
                        )}
                        style={tab.style}
                      >
                        {tab.label}
                      </Badge>
                      {count > 0 && (
                        <span className="absolute -top-2 -right-2.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-bold leading-none text-background">
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => setColumnSettingsOpen(true)}
              className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              title="Sütun Ayarları"
            >
              <Settings2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        {(searchable || toolbarActions || toolbarRight || onAddNew) && (
          <div className="border-b p-4">
            <div className="flex items-center gap-3">
              {searchable && (
                <div className="relative max-w-sm">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={onExternalSearch ? (externalSearchValue ?? '') : globalFilter}
                    onChange={(e) => onExternalSearch ? onExternalSearch(e.target.value) : setGlobalFilter(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="pl-9"
                  />
                </div>
              )}
              {/* Bulk actions / toolbar actions */}
              {toolbarActions}
              {/* Active column filter badges */}
              {activeColumnFilters.map((f) => {
                const col = columns.find((c) => (c as any).accessorKey === f.id || (c as any).id === f.id);
                const colName = typeof col?.header === 'string' ? col.header : f.id;
                const v = f.value as any;

                let badgeText = '';
                let ModeIcon: typeof CircleCheck | null = null;

                if (v && typeof v === 'object' && 'mode' in v && 'values' in v) {
                  // SelectFilterValue
                  const sfv = v as SelectFilterValue;
                  const filterOpts = (col as any)?.meta?.filterOptions as DataTableFilterOption[] | undefined;
                  const labels = sfv.values.map((val: string) => filterOpts?.find((o) => o.value === val)?.label ?? val);
                  ModeIcon = sfv.mode === 'include' ? CircleCheck : CircleX;
                  badgeText = labels.join(', ');
                } else if (v && typeof v === 'object' && 'mode' in v && 'value' in v) {
                  // NumberFilterValue
                  const nfv = v as NumberFilterValue;
                  const modeSymbol = nfv.mode === 'eq' ? '=' : nfv.mode === 'lt' ? '<' : '>';
                  badgeText = `${modeSymbol} ${nfv.value.toLocaleString('tr-TR')}`;
                } else if (v && typeof v === 'object' && ('from' in v || 'to' in v)) {
                  // DateFilterValue
                  const dfv = v as DateFilterValue;
                  const fmt = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('tr-TR');
                  if (dfv.from && dfv.to) badgeText = `${fmt(dfv.from)} - ${fmt(dfv.to)}`;
                  else if (dfv.from) badgeText = `${fmt(dfv.from)}'den`;
                  else if (dfv.to) badgeText = `${fmt(dfv.to)}'e kadar`;
                  ModeIcon = CalendarIcon as any;
                } else if (Array.isArray(v)) {
                  const filterOpts = (col as any)?.meta?.filterOptions as DataTableFilterOption[] | undefined;
                  badgeText = v.map((val: string) => filterOpts?.find((o) => o.value === val)?.label ?? val).join(', ');
                }

                return (
                  <div key={f.id} className="flex items-center gap-1 rounded-md border bg-muted/50 px-2 py-1 text-xs">
                    {ModeIcon && <ModeIcon className="h-3 w-3 text-muted-foreground" />}
                    <span className="font-medium">{colName}:</span>
                    <span>{badgeText}</span>
                    <button
                      type="button"
                      onClick={() => setColumnFilters((prev) => prev.filter((cf) => cf.id !== f.id))}
                      className="ml-1 rounded-sm hover:bg-muted"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
              {/* Spacer */}
              <div className="flex-1" />
              {toolbarRight && (() => {
                const hasFilters = activeFilter !== null || columnFilters.length > 0 || globalFilter !== '';
                return toolbarRight(table.getFilteredRowModel().rows.map((r) => r.original), hasFilters);
              })()}
              {onAddNew && (
                <Button size="sm" onClick={onAddNew}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  {addNewLabel}
                </Button>
              )}
            </div>
          </div>
        )}
        <VirtualizedTableContainer
          virtualized={virtualized}
          virtualContainerHeight={virtualContainerHeight}
          virtualRowHeight={virtualRowHeight}
          loading={loading}
          table={table}
          allColumns={allColumns}
          onRowClick={onRowClick}
          onRowHover={onRowHover}
          onRowHoverEnd={onRowHoverEnd}
          renderSubRow={renderSubRow}
          emptyMessage={emptyMessage}
          infiniteScroll={infiniteScroll}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
        />

        {/* Infinite scroll sentinel + loading */}
        {infiniteScroll && (
          <div className="flex justify-center py-3">
            {loadingMore ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                <span>Yükleniyor...</span>
              </div>
            ) : hasMore ? (
              <div ref={sentinelRef} className="h-1" />
            ) : table.getFilteredRowModel().rows.length > 0 ? (
              <span className="text-xs text-muted-foreground">Tüm kayıtlar yüklendi</span>
            ) : null}
          </div>
        )}

        {/* Footer Content */}
        {footerContent && (() => {
          const hasFilters = activeFilter !== null || columnFilters.length > 0 || globalFilter !== '';
          return (
            <div className="border-t px-4 py-2">
              {footerContent(table.getFilteredRowModel().rows.map((r) => r.original), hasFilters)}
            </div>
          );
        })()}

        {/* Server-side pagination */}
        {serverPagination && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {selectable && Object.keys(rowSelection).length > 0 && (
                <>
                  <span className="font-medium text-foreground">
                    {Object.keys(rowSelection).length} seçili
                  </span>
                  <span>|</span>
                </>
              )}
              <span>
                {serverPagination.totalRows > 0
                  ? `${serverPagination.totalRows.toLocaleString('tr-TR')} kayıttan ${((serverPagination.currentPage - 1) * pageSize + 1)}-${Math.min((serverPagination.currentPage - 1) * pageSize + data.length, serverPagination.totalRows)} gösteriliyor`
                  : 'Kayıt bulunamadı'}
              </span>
            </div>
            {serverPagination.totalPages > 1 && (
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => serverPagination.onPageChange(1)}
                  disabled={serverPagination.currentPage <= 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => serverPagination.onPageChange(serverPagination.currentPage - 1)}
                  disabled={serverPagination.currentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="mx-2 text-sm text-muted-foreground">
                  {serverPagination.currentPage} / {serverPagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => serverPagination.onPageChange(serverPagination.currentPage + 1)}
                  disabled={serverPagination.currentPage >= serverPagination.totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => serverPagination.onPageChange(serverPagination.totalPages)}
                  disabled={serverPagination.currentPage >= serverPagination.totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Client-side pagination — hidden in infinite scroll and server pagination modes */}
        {!infiniteScroll && !serverPagination && filteredData.length > pageSizeOptions[0] && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {selectable && Object.keys(rowSelection).length > 0 && (
                <span className="font-medium text-foreground">
                  {Object.keys(rowSelection).length} seçili
                </span>
              )}
              {selectable && Object.keys(rowSelection).length > 0 && <span>|</span>}
              <span>Sayfa başına</span>
              <Select
                value={String(table.getState().pagination.pageSize)}
                onValueChange={(value) => table.setPageSize(Number(value))}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span>
                | {table.getFilteredRowModel().rows.length} kayıttan{' '}
                {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                  table.getFilteredRowModel().rows.length
                )}{' '}
                gösteriliyor
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="mx-2 text-sm text-muted-foreground">
                {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    <ColumnSettingsSheet
      open={columnSettingsOpen}
      onClose={() => setColumnSettingsOpen(false)}
      table={table}
      columnOrder={columnOrder}
      setColumnOrder={setColumnOrder}
    />
    </>
  );
}

// Column settings sheet with drag-and-drop reordering
function ColumnSettingsSheet<TData>({
  open,
  onClose,
  table,
  columnOrder,
  setColumnOrder,
}: {
  open: boolean;
  onClose: () => void;
  table: ReturnType<typeof useReactTable<TData>>;
  columnOrder: ColumnOrderState;
  setColumnOrder: React.Dispatch<React.SetStateAction<ColumnOrderState>>;
}) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const snapshotRef = useRef<Map<string, number>>(new Map());
  const lockRef = useRef(false);

  // Get settable column ids (exclude utility columns) in current order
  const orderedSettableIds = useMemo(() => {
    const allCols = table.getAllLeafColumns();
    const colMap = new Map(allCols.map((c) => [c.id, c]));
    const ordered = columnOrder.filter((id) => !UTILITY_COLUMNS.has(id) && colMap.get(id)?.getCanHide());
    for (const col of allCols) {
      if (!UTILITY_COLUMNS.has(col.id) && col.getCanHide() && !ordered.includes(col.id)) {
        ordered.push(col.id);
      }
    }
    return ordered;
  }, [table, columnOrder]);

  // FLIP: capture clean positions (strip any leftover transforms)
  const capturePositions = useCallback(() => {
    const snap = new Map<string, number>();
    itemRefs.current.forEach((el, id) => {
      // Clear any in-flight transform so we read the true layout position
      el.style.transition = 'none';
      el.style.transform = '';
      snap.set(id, el.getBoundingClientRect().top);
    });
    snapshotRef.current = snap;
  }, []);

  // FLIP: after React re-renders, animate from old → new positions
  useLayoutEffect(() => {
    if (snapshotRef.current.size === 0) return;
    const snapshot = snapshotRef.current;
    snapshotRef.current = new Map();

    let animatedCount = 0;

    itemRefs.current.forEach((el, id) => {
      const oldTop = snapshot.get(id);
      if (oldTop === undefined) return;
      const newTop = el.getBoundingClientRect().top;
      const delta = oldTop - newTop;
      if (delta === 0) return;

      animatedCount++;
      // Invert: jump to old position
      el.style.transition = 'none';
      el.style.transform = `translateY(${delta}px)`;
    });

    if (animatedCount === 0) {
      lockRef.current = false;
      return;
    }

    // Force reflow so the browser registers the "inverted" position
    document.body.offsetHeight;

    // Play: animate to final position
    itemRefs.current.forEach((el) => {
      if (el.style.transform && el.style.transform !== '') {
        el.style.transition = 'transform 180ms cubic-bezier(0.2, 0, 0, 1)';
        el.style.transform = '';
      }
    });

    // Unlock after animation completes
    const timer = setTimeout(() => { lockRef.current = false; }, 190);
    return () => clearTimeout(timer);
  }, [orderedSettableIds]);

  const doMove = useCallback((updater: (prev: ColumnOrderState) => ColumnOrderState) => {
    if (lockRef.current) return;
    lockRef.current = true;
    capturePositions();
    setColumnOrder(updater);
  }, [setColumnOrder, capturePositions]);

  const moveColumn = useCallback((fromId: string, toId: string) => {
    if (fromId === toId) return;
    doMove((prev) => {
      const next = [...prev];
      const fromIdx = next.indexOf(fromId);
      const toIdx = next.indexOf(toId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromId);
      return next;
    });
  }, [doMove]);

  const moveUp = useCallback((id: string) => {
    doMove((prev) => {
      const settable = prev.filter((cid) => !UTILITY_COLUMNS.has(cid));
      const idx = settable.indexOf(id);
      if (idx <= 0) return prev;
      const prevId = settable[idx - 1];
      const next = [...prev];
      const aIdx = next.indexOf(id);
      const bIdx = next.indexOf(prevId);
      next[aIdx] = prevId;
      next[bIdx] = id;
      return next;
    });
  }, [doMove]);

  const moveDown = useCallback((id: string) => {
    doMove((prev) => {
      const settable = prev.filter((cid) => !UTILITY_COLUMNS.has(cid));
      const idx = settable.indexOf(id);
      if (idx === -1 || idx >= settable.length - 1) return prev;
      const nextId = settable[idx + 1];
      const next = [...prev];
      const aIdx = next.indexOf(id);
      const bIdx = next.indexOf(nextId);
      next[aIdx] = nextId;
      next[bIdx] = id;
      return next;
    });
  }, [doMove]);

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };

  // Live reorder on hover — locked during animation
  const handleDragOver = useCallback((e: React.DragEvent, id: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedId && id !== draggedId) {
      moveColumn(draggedId, id);
    }
  }, [draggedId, moveColumn]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedId(null);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
  };

  const setItemRef = useCallback((id: string) => (el: HTMLDivElement | null) => {
    if (el) itemRefs.current.set(id, el);
    else itemRefs.current.delete(id);
  }, []);

  return (
    <SlideOverSheet
      open={open}
      onClose={onClose}
      title="Sütun Ayarları"
      description="Sütunları açıp kapatın veya sürükleyerek sıralayın"
      size="sm"
    >
      <div className="space-y-0.5">
        {orderedSettableIds.map((colId, idx) => {
          const col = table.getColumn(colId);
          if (!col) return null;
          const header = col.columnDef.header;
          const label = typeof header === 'string' ? header : colId;
          const isFirst = idx === 0;
          const isLast = idx === orderedSettableIds.length - 1;
          const isDragged = draggedId === colId;

          return (
            <div
              key={colId}
              ref={setItemRef(colId)}
              draggable
              onDragStart={(e) => handleDragStart(e, colId)}
              onDragOver={(e) => handleDragOver(e, colId)}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              className={cn(
                'flex items-center gap-2 rounded-lg border px-3 py-2.5 will-change-transform',
                isDragged
                  ? 'opacity-30 scale-[0.97] border-dashed border-primary/40 bg-primary/5'
                  : 'hover:bg-muted/50',
              )}
              style={{ transition: isDragged ? 'opacity 150ms, transform 150ms' : undefined }}
            >
              <div className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
                <GripVertical className="h-4 w-4" />
              </div>
              <span className="flex-1 text-sm font-medium select-none">{label}</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={isFirst}
                  onClick={() => moveUp(colId)}
                  className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-20 disabled:pointer-events-none"
                  title="Yukarı taşı"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  disabled={isLast}
                  onClick={() => moveDown(colId)}
                  className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-20 disabled:pointer-events-none"
                  title="Aşağı taşı"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <Switch
                  checked={col.getIsVisible()}
                  onCheckedChange={(value) => col.toggleVisibility(!!value)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </SlideOverSheet>
  );
}

// Select column filter popover (include / exclude mode)
// Supports grouped options with tabs when options have `group` field
function SelectFilterPopover({
  options,
  filterValue,
  onChange,
}: {
  options: DataTableFilterOption[];
  filterValue: SelectFilterValue | undefined;
  onChange: (val: SelectFilterValue | undefined) => void;
}) {
  const selectedValues = filterValue?.values ?? [];
  const hasActiveFilter = selectedValues.length > 0;
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // Build groups (always called for hooks consistency)
  const groups = useMemo(() => {
    const seen = new Map<string, DataTableFilterOption[]>();
    for (const opt of options) {
      const g = opt.group ?? '';
      if (!seen.has(g)) seen.set(g, []);
      seen.get(g)!.push(opt);
    }
    return [...seen.entries()].map(([name, items]) => ({ name, items }));
  }, [options]);

  const hasGroups = groups.length > 1 || (groups.length === 1 && groups[0].name !== '');

  // ─── Grouped (tabbed) filter ───
  if (hasGroups) {
    const groupModes = filterValue?.groupModes ?? {};
    const currentGroup = groups[activeTab] ?? groups[0];
    const isNumberGroup = currentGroup.items.some((o) => o.groupFilterType === 'number');
    const currentMode = groupModes[currentGroup.name] ?? 'include';
    const nf = filterValue?.numberFilter;

    const filteredOpts = search
      ? currentGroup.items.filter((o) => o.label.toLocaleLowerCase('tr').includes(search.toLocaleLowerCase('tr')))
      : currentGroup.items;

    const groupHasFilter = (groupName: string) => {
      if (filterValue == null) return false;
      const grp = groups.find((g) => g.name === groupName);
      if (!grp) return false;
      const isNum = grp.items.some((o) => o.groupFilterType === 'number');
      if (isNum) {
        const numF = filterValue.numberFilter;
        return numF != null && typeof numF.value === 'number' && !isNaN(numF.value);
      }
      const vals = filterValue.values;
      if (!Array.isArray(vals) || vals.length === 0) return false;
      return vals.some((v) => grp.items.some((o) => o.value === v));
    };

    const emit = (patch: Partial<SelectFilterValue>) => {
      const base: SelectFilterValue = {
        mode: filterValue?.mode ?? 'include',
        values: filterValue?.values ?? [],
        groupModes: filterValue?.groupModes,
        numberFilter: filterValue?.numberFilter,
        ...patch,
      };
      const hasValues = base.values.length > 0;
      const hasNumber = base.numberFilter != null && typeof base.numberFilter.value === 'number' && !isNaN(base.numberFilter.value);
      const hasGroupModes = base.groupModes && Object.keys(base.groupModes).length > 0;
      if (!hasValues && !hasNumber && !hasGroupModes) onChange(undefined);
      else onChange(base);
    };

    const setGroupMode = (m: 'include' | 'exclude') => {
      emit({ groupModes: { ...groupModes, [currentGroup.name]: m } });
    };

    const toggle = (value: string) => {
      const next = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      emit({ values: next });
    };

    const setNumberMode = (m: 'eq' | 'lt' | 'gt') => {
      if (nf) emit({ numberFilter: { ...nf, mode: m } });
    };

    const setNumberValue = (v: string) => {
      const num = v === '' ? undefined : Number(v);
      if (num === undefined || isNaN(num)) emit({ numberFilter: undefined });
      else emit({ numberFilter: { mode: nf?.mode ?? 'eq', value: num } });
    };

    const clearGroup = (groupName: string) => {
      const grp = groups.find((g) => g.name === groupName);
      if (!grp) return;
      const isNum = grp.items.some((o) => o.groupFilterType === 'number');
      if (isNum) {
        const newModes = { ...groupModes };
        delete newModes[groupName];
        emit({ numberFilter: undefined, groupModes: newModes });
      } else {
        const grpValues = new Set(grp.items.map((o) => o.value));
        const next = selectedValues.filter((v) => !grpValues.has(v));
        const newModes = { ...groupModes };
        delete newModes[groupName];
        emit({ values: next, groupModes: newModes });
      }
    };

    const hasAnyFilter = filterValue != null && (
      (Array.isArray(filterValue.values) && filterValue.values.length > 0) ||
      (filterValue.numberFilter != null && typeof filterValue.numberFilter.value === 'number')
    );
    const activeGroupHasFilter = groupHasFilter(currentGroup.name);
    const otherGroupsWithFilter = groups.filter((g) => g.name !== currentGroup.name && groupHasFilter(g.name));
    const multipleGroupsFiltered = activeGroupHasFilter && otherGroupsWithFilter.length > 0;

    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn('rounded p-0.5 transition-colors hover:bg-muted', hasAnyFilter && 'text-primary')}
          >
            <Filter className={cn('h-3.5 w-3.5', hasAnyFilter ? 'opacity-100' : 'opacity-40')} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          {/* Group tabs */}
          <div className="flex border-b">
            {groups.map((g, i) => (
              <button
                key={g.name}
                type="button"
                onClick={() => { setActiveTab(i); setSearch(''); }}
                className={cn(
                  'flex-1 px-3 py-2 text-xs font-medium transition-colors',
                  activeTab === i
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {g.name}
              </button>
            ))}
          </div>
          {isNumberGroup ? (
            <>
              {/* Number filter: mode selector */}
              <div className="flex gap-1 p-2 border-b">
                <button
                  type="button"
                  onClick={() => setNumberMode('eq')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                    (nf?.mode ?? 'eq') === 'eq'
                      ? 'text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                  title="Eşit"
                >
                  <Equal className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setNumberMode('lt')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                    nf?.mode === 'lt'
                      ? 'text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                  title="Küçük"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setNumberMode('gt')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                    nf?.mode === 'gt'
                      ? 'text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                  title="Büyük"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
              </div>
              {/* Number filter: value input */}
              <div className="p-2">
                <Input
                  type="number"
                  placeholder="Tutar girin..."
                  value={nf?.value ?? ''}
                  onChange={(e) => setNumberValue(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </>
          ) : (
            <>
              {/* Select filter: mode selector */}
              <div className="flex gap-1 p-2 border-b">
                <button
                  type="button"
                  onClick={() => setGroupMode('include')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                    currentMode === 'include'
                      ? 'text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <CircleCheck className="h-3.5 w-3.5" />
                  Sadece seçilenler
                </button>
                <button
                  type="button"
                  onClick={() => setGroupMode('exclude')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                    currentMode === 'exclude'
                      ? 'text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <CircleX className="h-3.5 w-3.5" />
                  Seçilenler hariç
                </button>
              </div>
              {/* Select filter: search */}
              <div className="p-2 border-b">
                <Input
                  placeholder="Ara..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
              {/* Select filter: options */}
              <div className="p-2 space-y-1 max-h-[340px] overflow-y-auto">
                {filteredOpts.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-2">Sonuç bulunamadı</p>
                ) : (
                  filteredOpts.map((opt) => (
                    <label
                      key={opt.value}
                      className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-muted"
                    >
                      <Checkbox
                        checked={selectedValues.includes(opt.value)}
                        onCheckedChange={() => toggle(opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))
                )}
              </div>
            </>
          )}
          {/* Clear buttons */}
          {hasAnyFilter && (
            <div className="border-t p-2 space-y-1">
              {multipleGroupsFiltered && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => clearGroup(currentGroup.name)}
                >
                  {currentGroup.name} Filtresini Temizle
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                onClick={() => onChange(undefined)}
              >
                {multipleGroupsFiltered
                  ? `${groups.filter((g) => groupHasFilter(g.name)).map((g) => g.name).join(' ve ')} Filtrelerini Temizle`
                  : 'Filtreyi Temizle'}
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    );
  }

  // ─── Flat (non-grouped) filter ───
  const mode = filterValue?.mode ?? 'include';

  const setMode = (m: 'include' | 'exclude') => {
    onChange({ mode: m, values: selectedValues });
  };

  const toggle = (value: string) => {
    const next = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    if (next.length === 0) {
      onChange(undefined);
    } else {
      onChange({ mode, values: next });
    }
  };

  const filteredOptions = search
    ? options.filter((opt) => opt.label.toLocaleLowerCase('tr').includes(search.toLocaleLowerCase('tr')))
    : options;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn('rounded p-0.5 transition-colors hover:bg-muted', hasActiveFilter && 'text-primary')}
        >
          <Filter className={cn('h-3.5 w-3.5', hasActiveFilter ? 'opacity-100' : 'opacity-40')} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-0" align="start">
        {/* Mode selector */}
        <div className="flex gap-1 p-2 border-b">
          <button
            type="button"
            onClick={() => setMode('include')}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
              mode === 'include'
                ? 'text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <CircleCheck className="h-3.5 w-3.5" />
            Sadece seçilenler
          </button>
          <button
            type="button"
            onClick={() => setMode('exclude')}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
              mode === 'exclude'
                ? 'text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <CircleX className="h-3.5 w-3.5" />
            Seçilenler hariç
          </button>
        </div>
        {/* Search */}
        <div className="p-2 border-b">
          <Input
            placeholder="Ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs"
          />
        </div>
        {/* Options */}
        <div className="p-2 space-y-1 max-h-[340px] overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-2">Sonuç bulunamadı</p>
          ) : (
            filteredOptions.map((opt) => (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-muted"
              >
                <Checkbox
                  checked={selectedValues.includes(opt.value)}
                  onCheckedChange={() => toggle(opt.value)}
                />
                {opt.label}
              </label>
            ))
          )}
        </div>
        {hasActiveFilter && (
          <div className="border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={() => onChange(undefined)}
            >
              Filtreyi Temizle
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Preset definitions for date range picker
type DatePreset = {
  label: string;
  getRange: () => { from: Date; to: Date };
};

const DATE_PRESETS: DatePreset[] = [
  {
    label: 'Bugün',
    getRange: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }),
  },
  {
    label: 'Dün',
    getRange: () => {
      const yesterday = subDays(new Date(), 1);
      return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
    },
  },
  {
    label: 'Bugün ve dün',
    getRange: () => ({ from: startOfDay(subDays(new Date(), 1)), to: endOfDay(new Date()) }),
  },
  {
    label: 'Son 7 gün',
    getRange: () => ({ from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date()) }),
  },
  {
    label: 'Son 14 gün',
    getRange: () => ({ from: startOfDay(subDays(new Date(), 13)), to: endOfDay(new Date()) }),
  },
  {
    label: 'Son 28 gün',
    getRange: () => ({ from: startOfDay(subDays(new Date(), 27)), to: endOfDay(new Date()) }),
  },
  {
    label: 'Son 30 gün',
    getRange: () => ({ from: startOfDay(subDays(new Date(), 29)), to: endOfDay(new Date()) }),
  },
  {
    label: 'Bu hafta',
    getRange: () => ({ from: startOfWeek(new Date(), { locale: tr }), to: endOfDay(new Date()) }),
  },
  {
    label: 'Geçen hafta',
    getRange: () => {
      const lastWeek = subWeeks(new Date(), 1);
      return { from: startOfWeek(lastWeek, { locale: tr }), to: endOfWeek(lastWeek, { locale: tr }) };
    },
  },
  {
    label: 'Bu ay',
    getRange: () => ({ from: startOfMonth(new Date()), to: endOfDay(new Date()) }),
  },
  {
    label: 'Geçen ay',
    getRange: () => {
      const lastMonth = subMonths(new Date(), 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    },
  },
  {
    label: 'Maksimum',
    getRange: () => ({ from: new Date(2020, 0, 1), to: endOfDay(new Date()) }),
  },
];

function toISODate(d: Date): string {
  return formatDate2(d, 'yyyy-MM-dd');
}

function fromISODate(s: string): Date {
  return new Date(s + 'T00:00:00');
}

// Date range filter popover with presets and dual calendar
function DateFilterPopover({
  filterValue,
  onChange,
}: {
  filterValue: DateFilterValue | undefined;
  onChange: (val: DateFilterValue | undefined) => void;
}) {
  const hasActiveFilter = !!(filterValue?.from || filterValue?.to);
  const [open, setOpen] = useState(false);

  // Internal state for pending selection (applied on "Güncelle")
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>(undefined);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());

  // Sync internal state when popover opens
  useEffect(() => {
    if (open) {
      const from = filterValue?.from ? fromISODate(filterValue.from) : undefined;
      const to = filterValue?.to ? fromISODate(filterValue.to) : undefined;
      setPendingRange(from || to ? { from, to } : undefined);
      setSelectedPreset(null);
      // Show month of 'from' date or previous month for dual view
      if (from) {
        setCalendarMonth(subMonths(from, 0));
      } else {
        setCalendarMonth(subMonths(new Date(), 1));
      }
    }
  }, [open]);

  function handlePresetClick(preset: DatePreset) {
    const range = preset.getRange();
    setPendingRange({ from: range.from, to: range.to });
    setSelectedPreset(preset.label);
    setCalendarMonth(subMonths(range.to, 1));
  }

  function handleApply() {
    if (pendingRange?.from && pendingRange?.to) {
      onChange({
        from: toISODate(pendingRange.from),
        to: toISODate(pendingRange.to),
      });
    } else if (pendingRange?.from) {
      onChange({ from: toISODate(pendingRange.from) });
    } else {
      onChange(undefined);
    }
    setOpen(false);
  }

  function handleCancel() {
    setOpen(false);
  }

  function handleClear() {
    onChange(undefined);
    setOpen(false);
  }

  const formatDisplay = (d: Date) => formatDate2(d, 'd MMMM yyyy', { locale: tr });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'rounded p-0.5 transition-colors hover:bg-muted',
            hasActiveFilter && 'text-primary'
          )}
        >
          <CalendarIcon className={cn('h-3.5 w-3.5', hasActiveFilter ? 'opacity-100' : 'opacity-40')} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" sideOffset={8}>
        <div className="flex">
          {/* Left sidebar - presets */}
          <div className="border-r py-3 px-1 min-w-[140px]">
            <div className="space-y-0.5">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => handlePresetClick(preset)}
                  className={cn(
                    'flex items-center gap-2 w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors',
                    selectedPreset === preset.label
                      ? 'bg-accent text-accent-foreground font-medium'
                      : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                  )}
                >
                  <div className={cn(
                    'h-3 w-3 rounded-full border-2 flex items-center justify-center shrink-0',
                    selectedPreset === preset.label ? 'border-primary' : 'border-muted-foreground/40'
                  )}>
                    {selectedPreset === preset.label && (
                      <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                    )}
                  </div>
                  {preset.label}
                </button>
              ))}
              {/* Özel (custom) */}
              <button
                type="button"
                onClick={() => setSelectedPreset(null)}
                className={cn(
                  'flex items-center gap-2 w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors',
                  selectedPreset === null && pendingRange
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                )}
              >
                <div className={cn(
                  'h-3 w-3 rounded-full border-2 flex items-center justify-center shrink-0',
                  selectedPreset === null && pendingRange ? 'border-primary' : 'border-muted-foreground/40'
                )}>
                  {selectedPreset === null && pendingRange && (
                    <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  )}
                </div>
                Özel
              </button>
            </div>
          </div>

          {/* Right side - calendars + footer */}
          <div className="flex flex-col">
            <div className="p-3">
              <Calendar
                mode="range"
                selected={pendingRange}
                onSelect={(range) => {
                  setPendingRange(range);
                  setSelectedPreset(null);
                }}
                numberOfMonths={2}
                month={calendarMonth}
                onMonthChange={setCalendarMonth}
              />
            </div>

            {/* Footer */}
            <div className="border-t px-4 py-3 flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                {pendingRange?.from && pendingRange?.to ? (
                  <span>{formatDisplay(pendingRange.from)} - {formatDisplay(pendingRange.to)}</span>
                ) : pendingRange?.from ? (
                  <span>{formatDisplay(pendingRange.from)} - ...</span>
                ) : (
                  <span>Tarih aralığı seçin</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {hasActiveFilter && (
                  <Button variant="ghost" size="sm" onClick={handleClear}>
                    Temizle
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleCancel}>
                  İptal
                </Button>
                <Button size="sm" onClick={handleApply} disabled={!pendingRange?.from}>
                  Güncelle
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Number column filter popover (eq / lt / gt mode)
function NumberFilterPopover({
  filterValue,
  onChange,
}: {
  filterValue: NumberFilterValue | undefined;
  onChange: (val: NumberFilterValue | undefined) => void;
}) {
  const mode = filterValue?.mode ?? 'eq';
  const value = filterValue?.value ?? '';
  const hasActiveFilter = filterValue !== undefined;

  const setMode = (m: 'eq' | 'lt' | 'gt') => {
    if (value !== '' && value !== undefined) {
      onChange({ mode: m, value: Number(value) });
    }
  };

  const setValue = (v: string) => {
    const num = v === '' ? undefined : Number(v);
    if (num === undefined || isNaN(num)) {
      onChange(undefined);
    } else {
      onChange({ mode, value: num });
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'rounded p-0.5 transition-colors hover:bg-muted',
            hasActiveFilter && 'text-primary'
          )}
        >
          <Filter className={cn('h-3.5 w-3.5', hasActiveFilter ? 'opacity-100' : 'opacity-40')} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-0" align="start">
        {/* Mode selector */}
        <div className="flex gap-1 p-2 border-b">
          <button
            type="button"
            onClick={() => setMode('eq')}
            className={cn(
              'flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
              mode === 'eq'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
            title="Eşit"
          >
            <Equal className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setMode('lt')}
            className={cn(
              'flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
              mode === 'lt'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
            title="Küçük"
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setMode('gt')}
            className={cn(
              'flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
              mode === 'gt'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
            title="Büyük"
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
        </div>
        {/* Value input */}
        <div className="p-2">
          <Input
            type="number"
            placeholder="Tutar girin..."
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        {hasActiveFilter && (
          <div className="border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={() => onChange(undefined)}
            >
              Filtreyi Temizle
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// --- Virtualized Table Container ---
// Hem normal hem virtualized modu destekler.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function VirtualizedTableContainer<TData>({
  virtualized,
  virtualContainerHeight,
  virtualRowHeight,
  loading,
  table,
  allColumns,
  onRowClick,
  onRowHover,
  onRowHoverEnd,
  renderSubRow,
  emptyMessage,
  infiniteScroll,
  hasMore,
  onLoadMore,
}: {
  virtualized: boolean;
  virtualContainerHeight: number;
  virtualRowHeight: number;
  loading: boolean;
  table: any;
  allColumns: any[];
  onRowClick?: (row: TData) => void;
  onRowHover?: (row: TData) => void;
  onRowHoverEnd?: () => void;
  renderSubRow?: (row: TData) => ReactNode;
  emptyMessage: string;
  infiniteScroll: boolean;
  hasMore: boolean;
  onLoadMore?: () => void;
}) {
  const rows = table.getRowModel().rows as Row<TData>[];
  const parentRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  // Dinamik yükseklik: mount sonrası tablonun konumunu ölçüp viewport'un geri kalanını doldur
  const [dynamicHeight, setDynamicHeight] = useState(virtualContainerHeight);
  useEffect(() => {
    function updateHeight() {
      const el = parentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const available = window.innerHeight - rect.top - 40;
      setDynamicHeight(Math.max(400, available));
    }
    // İlk ölçüm: requestAnimationFrame ile DOM stabilize olduktan sonra
    const raf = requestAnimationFrame(updateHeight);
    window.addEventListener('resize', updateHeight);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', updateHeight) };
  }, []);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => virtualRowHeight,
    overscan: 10,
    enabled: virtualized && rows.length > 0,
    getItemKey: (index) => rows[index]?.id ?? String(index),
  });

  // Auto-load more when scrolling near bottom (virtualized + infinite scroll)
  useEffect(() => {
    if (!virtualized || !infiniteScroll || !hasMore) return;
    const el = parentRef.current;
    if (!el) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      // scrollHeight <= clientHeight: virtualizer henüz render etmedi veya içerik sığıyor — tetikleme
      if (scrollHeight <= clientHeight) return;
      if (scrollHeight - scrollTop - clientHeight < 300) {
        onLoadMoreRef.current?.();
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    // Kullanıcı veri yüklenirken scroll ettiyse yakalamak için geciktirilmiş kontrol
    // (DOM stabilize olduktan sonra, false-positive önlenir)
    const raf = requestAnimationFrame(() => {
      if (el.scrollTop > 0) handleScroll();
    });
    return () => { el.removeEventListener('scroll', handleScroll); cancelAnimationFrame(raf); };
  }, [virtualized, infiniteScroll, hasMore]);

  // Header renderer (shared)
  const renderHeaderRow = (headerGroup: any) => (
    <TableRow key={headerGroup.id} className="bg-muted/50 hover:bg-muted/50">
      {headerGroup.headers.map((header: any) => {
        const meta = (header.column.columnDef as any).meta;
        const filterOptions = meta?.filterOptions as DataTableFilterOption[] | undefined;
        const filterType = meta?.filterType as 'number' | 'date' | undefined;
        const hasSelectFilter = filterOptions && filterOptions.length > 0;
        const hasNumberFilter = filterType === 'number';
        const hasDateFilter = filterType === 'date';

        return (
          <TableHead
            key={header.id}
            style={{ width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : undefined }}
          >
            {header.isPlaceholder ? null : (
              <div className="flex items-center gap-1">
                {header.column.getCanSort() ? (
                  <button
                    className="flex items-center gap-1 hover:text-foreground -ml-1 px-1 py-0.5 rounded transition-colors"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{
                      asc: <ArrowUp className="h-3.5 w-3.5" />,
                      desc: <ArrowDown className="h-3.5 w-3.5" />,
                    }[header.column.getIsSorted() as string] ?? <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />}
                  </button>
                ) : (
                  flexRender(header.column.columnDef.header, header.getContext())
                )}
                {hasSelectFilter && (
                  <SelectFilterPopover
                    options={filterOptions}
                    filterValue={header.column.getFilterValue() as SelectFilterValue | undefined}
                    onChange={(val: any) => header.column.setFilterValue(val)}
                  />
                )}
                {hasNumberFilter && (
                  <NumberFilterPopover
                    filterValue={header.column.getFilterValue() as NumberFilterValue | undefined}
                    onChange={(val: any) => header.column.setFilterValue(val)}
                  />
                )}
                {hasDateFilter && (
                  <DateFilterPopover
                    filterValue={header.column.getFilterValue() as DateFilterValue | undefined}
                    onChange={(val: any) => header.column.setFilterValue(val)}
                  />
                )}
              </div>
            )}
          </TableHead>
        );
      })}
    </TableRow>
  );

  // Row renderer (shared) — measureRef & dataIndex for virtualized scroll measurement
  const renderDataRow = (row: Row<TData>, index: number, measureRef?: (node: HTMLElement | null) => void, dataIndex?: number) => (
    <Fragment key={row.id}>
      <TableRow
        ref={measureRef}
        data-index={dataIndex}
        className={`${onRowClick ? 'cursor-pointer' : ''}`}
        onClick={() => onRowClick?.(row.original)}
        onMouseEnter={onRowHover ? () => onRowHover(row.original) : undefined}
        onMouseLeave={onRowHoverEnd || undefined}
      >
        {row.getVisibleCells().map((cell: any) => (
          <TableCell key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
      {renderSubRow && (() => {
        const subRowContent = renderSubRow(row.original);
        if (!subRowContent) return null;
        return (
          <TableRow className="bg-muted/30 hover:bg-muted/30">
            <TableCell colSpan={allColumns.length} className="px-4 py-3">
              {subRowContent}
            </TableCell>
          </TableRow>
        );
      })()}
    </Fragment>
  );

  // --- Virtualized rendering ---
  if (virtualized) {
    const virtualItems = virtualizer.getVirtualItems();
    const totalSize = virtualizer.getTotalSize();

    return (
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
          </div>
        )}
        <div
          ref={parentRef}
          // overflow-anchor: none — tarayıcının scroll-anchoring'i, viewport sınırında
          // yarım kalan bir satırın measureElement ile yeniden ölçülmesinden doğan
          // sub-pixel spacer değişimlerini "düzeltmek" için scrollTop'u oynatıyor;
          // bu da virtualizer'ı tekrar tetikleyip sonsuz ölçüm/titreme döngüsü yaratıyor.
          style={{ height: `${dynamicHeight}px`, overflow: 'auto', overflowAnchor: 'none' }}
        >
          <Table>
            <TableHeader className="sticky top-0 z-[1] bg-card">
              {table.getHeaderGroups().map(renderHeaderRow)}
            </TableHeader>
            <TableBody>
              {rows.length > 0 ? (
                <>
                  {virtualItems.length > 0 && virtualItems[0].start > 0 && (
                    <tr><td colSpan={allColumns.length} style={{ height: virtualItems[0].start }} /></tr>
                  )}
                  {virtualItems.map((virtualItem: any) => {
                    const row = rows[virtualItem.index];
                    if (!row) return null;
                    return renderDataRow(row, virtualItem.index, virtualizer.measureElement, virtualItem.index);
                  })}
                  {virtualItems.length > 0 && (() => {
                    const lastItem = virtualItems[virtualItems.length - 1];
                    const paddingBottom = totalSize - (lastItem ? lastItem.end : 0);
                    return paddingBottom > 0
                      ? <tr><td colSpan={allColumns.length} style={{ height: paddingBottom }} /></tr>
                      : null;
                  })()}
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={allColumns.length} className="h-24 text-center text-muted-foreground">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {infiniteScroll && hasMore && (
            <div className="flex justify-center py-3">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- Normal (non-virtualized) rendering ---
  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
        </div>
      )}
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(renderHeaderRow)}
        </TableHeader>
        <TableBody>
          {rows.length > 0 ? (
            rows.map((row, index) => renderDataRow(row, index))
          ) : (
            <TableRow>
              <TableCell colSpan={allColumns.length} className="h-24 text-center text-muted-foreground">
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
